// src/components/RoundScheduleEditor.js
// Shared round-schedule editor used by CreateEventsScreen and AdminBrandedEventsScreen.

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
} from 'react-native';
import {
  GAP_STEPS,
  formatRoundLocalTime,
  toEpochMs,
} from '../utils/roundSchedule';

export function RoundScheduleEditor({
  mode = 'gap',
  onModeChange,
  allowScheduled = true,
  interRoundGapMinutes = 5,
  onGapChange,
  totalRounds = 2,
  startAtMs = null,
  roundStartTimes = {},
  onRoundTimeChange,
  resetNotice = '',
  errorText = '',
  disabled = false,
  DatePickerComponent = null,
}) {
  const effectiveMode = allowScheduled ? mode : 'gap';
  const rounds = Math.max(2, Math.floor(Number(totalRounds)) || 2);
  const r1Ms = toEpochMs(startAtMs);
  const roundNumbers = [];
  for (let r = 2; r <= rounds; r++) {
    roundNumbers.push(r);
  }

  return (
    <View style={styles.container}>
      <View style={styles.choiceRow}>
        <TouchableOpacity
          style={[styles.choicePill, effectiveMode === 'gap' && styles.choicePillActive, disabled && styles.disabled]}
          onPress={() => onModeChange && onModeChange('gap')}
          disabled={disabled}
        >
          <Text style={[styles.choiceText, effectiveMode === 'gap' && styles.choiceTextActive]}>
            Same gap between rounds
          </Text>
        </TouchableOpacity>
        {allowScheduled && (
          <TouchableOpacity
            style={[styles.choicePill, effectiveMode === 'scheduled' && styles.choicePillActive, disabled && styles.disabled]}
            onPress={() => onModeChange && onModeChange('scheduled')}
            disabled={disabled}
          >
            <Text style={[styles.choiceText, effectiveMode === 'scheduled' && styles.choiceTextActive]}>
              {"Set each round's start time"}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {!allowScheduled && (
        <Text style={styles.exactStartHint}>
          {"Pick an exact start date to set each round's time."}
        </Text>
      )}

      {Boolean(resetNotice) && (
        <View style={styles.noticeBox}>
          <Text style={styles.noticeText}>{resetNotice}</Text>
        </View>
      )}

      {effectiveMode === 'gap' ? (
        <View style={styles.gapRow}>
          {GAP_STEPS.map((s) => {
            const active = interRoundGapMinutes === s.min;
            return (
              <TouchableOpacity
                key={s.min}
                label={s.label}
                style={[styles.gapPill, active && styles.gapPillActive, disabled && styles.disabled]}
                onPress={() => onGapChange && onGapChange(s.min)}
                disabled={disabled}
              >
                <Text style={[styles.gapPillText, active && styles.gapPillTextActive]}>
                  {s.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : (
        <View style={styles.rowsContainer}>
          <View style={styles.roundRowReadOnly}>
            <Text style={styles.roundLabel}>Round 1 starts</Text>
            <Text style={styles.roundReadOnlyValue}>{formatRoundLocalTime(r1Ms)}</Text>
          </View>

          {roundNumbers.map((r) => {
            const rawVal = roundStartTimes ? (roundStartTimes[r] ?? roundStartTimes[String(r)]) : null;
            const rowMs = toEpochMs(rawVal);
            return (
              <View key={`round_row_${r}`} style={styles.roundRow}>
                <View style={styles.roundHeaderRow}>
                  <Text style={styles.roundLabel}>{`Round ${r} starts`}</Text>
                  <Text style={styles.roundFormattedValue}>
                    {formatRoundLocalTime(rowMs)}
                  </Text>
                </View>
                {DatePickerComponent ? (
                  <DatePickerComponent
                    accessibilityLabel={`Round ${r} starts`}
                    round={r}
                    startMs={rowMs}
                    value={Number.isFinite(rowMs) ? new Date(rowMs) : null}
                    disabled={disabled}
                    onChange={(val) => onRoundTimeChange && onRoundTimeChange(r, val)}
                    onChangeText={(val) => onRoundTimeChange && onRoundTimeChange(r, val)}
                    style={styles.pickerStyle}
                  />
                ) : (
                  <TextInput
                    accessibilityLabel={`Round ${r} starts`}
                    round={r}
                    startMs={rowMs}
                    style={styles.textInput}
                    placeholder="YYYY-MM-DD HH:MM"
                    placeholderTextColor="#6e7688"
                    editable={!disabled}
                    onChange={(val) => onRoundTimeChange && onRoundTimeChange(r, val)}
                    onChangeText={(val) => onRoundTimeChange && onRoundTimeChange(r, val)}
                  />
                )}
              </View>
            );
          })}
        </View>
      )}

      {Boolean(errorText) && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{errorText}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 12,
    gap: 10,
  },
  choiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  choicePill: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2a2f36',
    backgroundColor: '#0f1115',
  },
  choicePillActive: {
    borderColor: '#ffd166',
    backgroundColor: '#231f10',
  },
  choiceText: {
    color: '#c9d1d9',
    fontSize: 13,
    fontWeight: '600',
  },
  choiceTextActive: {
    color: '#ffd166',
    fontWeight: '700',
  },
  exactStartHint: {
    color: '#9aa0a6',
    fontSize: 12,
  },
  gapRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  gapPill: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2a2f36',
    backgroundColor: '#0f1115',
  },
  gapPillActive: {
    borderColor: '#ffd166',
    backgroundColor: '#231f10',
  },
  gapPillText: {
    color: '#c9d1d9',
    fontSize: 13,
    fontWeight: '600',
  },
  gapPillTextActive: {
    color: '#ffd166',
    fontWeight: '700',
  },
  rowsContainer: {
    gap: 10,
    marginTop: 4,
  },
  roundRowReadOnly: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#0d0f12',
    borderWidth: 1,
    borderColor: '#222733',
  },
  roundRow: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#0d0f12',
    borderWidth: 1,
    borderColor: '#222733',
    gap: 6,
  },
  roundHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  roundLabel: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  roundReadOnlyValue: {
    color: '#ffd166',
    fontSize: 13,
    fontWeight: '600',
  },
  roundFormattedValue: {
    color: '#9aa0a6',
    fontSize: 12,
  },
  pickerStyle: {
    backgroundColor: '#0f1115',
    color: '#ffd166',
    borderColor: '#2a2f36',
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
    width: '100%',
  },
  textInput: {
    backgroundColor: '#0f1115',
    color: '#ffd166',
    borderColor: '#2a2f36',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 13,
  },
  noticeBox: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#101926',
    borderLeftWidth: 3,
    borderLeftColor: '#ffd166',
  },
  noticeText: {
    color: '#ffd166',
    fontSize: 12,
    fontWeight: '600',
  },
  errorBox: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#2a1215',
    borderLeftWidth: 3,
    borderLeftColor: '#ff6b6b',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: 12,
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.5,
  },
});

export default RoundScheduleEditor;
